import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Drawer } from "./CrmPanel";
import { COLUMNS, COL, START_COLUMN, defaultDelays, normalizeState, fold, plural, normHandle } from "./crmUtils";

const inputCls = "w-full rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-bordeaux";
const Btn = ({ primary, ...p }) => <button {...p} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${primary ? "bg-bordeaux text-white hover:bg-bordeaux-dark" : "border border-line bg-white text-ink-soft hover:border-bordeaux hover:text-bordeaux"}`} />;

function Shell({ title, sub, children, onClose, testid }) {
  return (
    <Drawer onClose={onClose} testid={testid}>
      <div className="space-y-5 p-6">
        <div><h3 className="font-sans text-xl font-medium text-ink">{title}</h3>{sub && <p className="mt-1 text-sm font-light text-ink-muted">{sub}</p>}</div>
        {children}
      </div>
    </Drawer>
  );
}

export function NewProspect({ state, onCreate, onClose }) {
  const [f, setF] = useState({ instagram: "", link: "" });
  const [msg, setMsg] = useState("");
  const [dupOk, setDupOk] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const ig = normHandle(f.instagram) || normHandle(f.link);
    if (!ig) { setMsg("Le @ Instagram est obligatoire."); return; }
    const dup = state.cards.find((c) => fold(c.instagram) === fold(ig));
    if (dup && dupOk !== ig) { setMsg(`@${ig} existe déjà dans « ${COL[dup.column].title} ». Clique à nouveau sur « Créer la carte » pour le créer quand même.`); setDupOk(ig); return; }
    const raw = /^https?:\/\//i.test(f.instagram.trim()) ? f.instagram.trim() : "";
    onCreate({ instagram: ig, link: f.link.trim() || raw });
  };
  return (
    <Shell testid="crm-new-modal" title="Nouveau prospect" sub={`La carte sera créée dans « ${COL[START_COLUMN].title} » avec la date et l'heure actuelles.`} onClose={onClose}>
      <form onSubmit={submit} className="grid gap-3">
        {[["instagram", "@ Instagram *", "@pseudo"], ["link", "Lien de son Instagram (facultatif)", "https://instagram.com/…"]].map(([k, l, p]) => (
          <label key={k}><span className="mb-1 block text-xs font-medium text-ink-muted">{l}</span><input data-testid={`crm-n-${k}`} className={inputCls} placeholder={p} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} autoFocus={k === "instagram"} /></label>
        ))}
        {msg && <p data-testid="crm-n-msg" className="rounded-xl bg-bordeaux-tint/50 px-3 py-2 text-xs text-bordeaux">{msg}</p>}
        <div className="flex justify-end gap-2"><Btn type="button" onClick={onClose}>Annuler</Btn><Btn primary type="submit" data-testid="crm-n-submit">Créer la carte</Btn></div>
      </form>
    </Shell>
  );
}

export function Settings({ state, onSave, onClose }) {
  const [d, setD] = useState(() => ({ ...defaultDelays(), ...state.settings.delays }));
  return (
    <Shell testid="crm-settings" title="Réglages des délais de relance" sub="Le délai démarre quand la carte arrive dans la colonne. Une fois dépassé, la carte passe en alerte." onClose={onClose}>
      <div className="grid gap-2">
        {COLUMNS.filter((c) => c.delay).map((c) => (
          <div key={c.id} className="grid grid-cols-[1fr_64px_88px] items-center gap-2 text-[13px]">
            <span className="flex items-center gap-2"><span className="h-2 w-2 shrink-0 rounded-full" style={{ background: c.color }} />{c.title}</span>
            <input data-testid={`crm-delay-${c.id}`} type="number" min="0" className={`${inputCls} !px-2 !py-1.5`} value={d[c.id].value} onChange={(e) => setD({ ...d, [c.id]: { ...d[c.id], value: e.target.value } })} />
            <select className={`${inputCls} !px-2 !py-1.5`} value={d[c.id].unit} onChange={(e) => setD({ ...d, [c.id]: { ...d[c.id], unit: e.target.value } })}><option value="j">jours</option><option value="h">heures</option></select>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Btn onClick={() => setD(defaultDelays())}>Valeurs par défaut</Btn><Btn onClick={onClose}>Annuler</Btn>
        <Btn primary data-testid="crm-settings-save" onClick={() => onSave(Object.fromEntries(Object.entries(d).map(([k, v]) => [k, { value: Math.max(0, Number(v.value) || 0), unit: v.unit }])))}>Enregistrer</Btn>
      </div>
    </Shell>
  );
}

export function Backup({ state, onImport, onClose }) {
  const file = useRef(null);
  const exp = () => {
    const blob = new Blob([JSON.stringify({ ...state, exportedAt: new Date().toISOString() }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `crm-sauvegarde-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Sauvegarde exportée");
  };
  const imp = async (f) => {
    let parsed;
    try { parsed = JSON.parse(await f.text()); if (!Array.isArray(parsed?.cards)) throw new Error("format"); } catch { toast.error("Fichier non reconnu : choisis un fichier exporté depuis ce CRM"); return; }
    if (!window.confirm(`Les ${plural(state.cards.length, "carte")} actuelles seront remplacées par les ${plural(parsed.cards.length, "carte")} du fichier. Importer ?`)) return;
    onImport(normalizeState(parsed));
  };
  return (
    <Shell testid="crm-backup" title="Sauvegarde" sub="Ton CRM est enregistré automatiquement dans ton espace. Tu peux aussi télécharger une copie." onClose={onClose}>
      <div className="grid gap-3">
        <div className="rounded-2xl border border-line bg-white p-4"><p className="mb-2 text-xs text-ink-muted">Télécharger toutes les cartes, notes, réglages et messages dans un fichier.</p><Btn data-testid="crm-export" onClick={exp}>Exporter une sauvegarde</Btn></div>
        <div className="rounded-2xl border border-line bg-white p-4"><p className="mb-2 text-xs text-ink-muted">Remplacer les données actuelles par celles d'un fichier de sauvegarde.</p><Btn data-testid="crm-import" onClick={() => file.current?.click()}>Importer une sauvegarde</Btn></div>
        <input ref={file} type="file" accept="application/json,.json" hidden onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) imp(f); }} />
      </div>
      <div className="flex justify-end"><Btn onClick={onClose}>Fermer</Btn></div>
    </Shell>
  );
}
